import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-zinc-200"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Logo dark={scrolled} />
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV_LINKS.map((link) => {
              const active = pathname === link.to;
              const tone = scrolled
                ? active
                  ? "text-black"
                  : "text-zinc-500 hover:text-black"
                : active
                  ? "text-cyan-400"
                  : "text-zinc-300 hover:text-white";
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={link.testId}
                  className={`text-sm font-medium transition-colors duration-300 ${tone}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-estimate-button"
              className="hidden lg:inline-flex items-center gap-1.5 rounded-full bg-cyan-400 text-black font-semibold text-sm px-5 py-2.5 hover:bg-black hover:text-white transition-colors duration-300"
            >
              Start a Project
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className={`lg:hidden w-11 h-11 grid place-items-center rounded-full border transition-colors ${
                scrolled
                  ? "border-zinc-300 text-black hover:border-cyan-500"
                  : "border-zinc-700 text-white hover:border-cyan-400"
              }`}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-overlay"
            className="fixed inset-0 z-40 bg-black flex flex-col justify-center px-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="grid-bg absolute inset-0 opacity-60" aria-hidden="true" />
            <nav className="relative flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-${link.testId}`}
                    className={`font-display text-5xl tracking-tight ${
                      pathname === link.to ? "text-cyan-400" : "text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
